import type { GiftGuide, GiftGuideProduct } from '@dupe/common/notion'

/**
 * Display derivations shared by the obsessions surfaces — the home picks cards
 * (`components/home/HomeObsessionCard`) and the `/obsessions` index
 * (`components/giftguide/Obsessions`). Kept in one place so the two surfaces
 * can never disagree about a collection's count or its "from" price.
 *
 * Everything here is curator-authored in Notion; nothing is computed from the
 * fetched slice when the API supplies a whole-collection value.
 */

/** Sale price wins whenever the curator has set one. */
export const productPrice = (p: GiftGuideProduct): number | undefined =>
  typeof p.salePrice === 'number' && p.salePrice > 0 ? p.salePrice : p.price

/** Lowest price for the collection. Prefers the server-computed minimum over
 *  the full product set; falls back to the cheapest fetched product. */
export const guideMinPrice = (g: GiftGuide): number | null => {
  if (typeof g.minPrice === 'number' && g.minPrice > 0) return g.minPrice
  const prices = (g.products || [])
    .map(productPrice)
    .filter((n): n is number => typeof n === 'number' && n > 0)
  return prices.length ? Math.min(...prices) : null
}

/** Whole-dollar, thousands-separated (`$1,344`). */
export const fmtPrice = (n: number | null | undefined): string | null =>
  typeof n === 'number' && n > 0
    ? `$${Math.round(n).toLocaleString('en-US')}`
    : null

/** Total finds in the collection — every product row, not just the fetched slice. */
export const guideCount = (g: GiftGuide): number =>
  g.productCount ?? (g.products || []).length

/** Products safe to put on a tile: an image is the minimum bar, and a named
 *  find also needs a title to sit under it. */
export const displayableProducts = (
  g: GiftGuide,
  opts: { requireTitle?: boolean } = {}
): GiftGuideProduct[] =>
  (g.products || []).filter(
    (p) => Boolean(p.image) && (!opts.requireTitle || Boolean(p.title))
  )
